/**
 * StatusPopup Component
 * Modal popup that displays all events for a given status in a PB DataTable.
 */

import React, {useCallback, useEffect, useMemo, useState} from 'react'; // useCallback kept for renderHeaders/renderRows

import {DataTable, Cell, TableHeader, Indent} from '@pb/kit-component-data-table';
import type {Column, CellOptions, RenderHeaders, RenderRows, RowData} from '@pb/kit-component-data-table';
import {Loading} from '@pb/kit-component-loading';

import {ROOT} from '../../middleware/api';

import {
    popupOverlay,
    popupContainer,
    popupHeader,
    popupTitle,
    popupCloseButton,
    popupBody,
    popupFooter,
    popupFooterClose,
    popupLoadingContainer,
    popupErrorContainer,
    popupEmptyContainer,
    popupTableWrapper,
    cellHighlight,
} from './status-popup.styles.module.styl';

// ── Column definitions matching the "Logs" popup ──

const POPUP_COLUMNS: Column[] = [
    {columnId: 'modifiedDate',  fixed: true,  resizable: true, defaultWidth: 130},
    {columnId: 'daName',        fixed: false, resizable: true, defaultWidth: 100},
    {columnId: 'eventName',     fixed: false, resizable: true, defaultWidth: 200},
    {columnId: 'eventType',     fixed: false, resizable: true, defaultWidth: 160},
    {columnId: 'beginDate',     fixed: false, resizable: true, defaultWidth: 130},
    {columnId: 'endDate',       fixed: false, resizable: true, defaultWidth: 130},
    {columnId: 'flag',          fixed: false, resizable: true, defaultWidth: 100},
    {columnId: 'eventYear',     fixed: false, resizable: true, defaultWidth: 90},
    {columnId: 'eventDetailUrl',fixed: false, resizable: true, defaultWidth: 140},
    {columnId: 'webcastUrl',    fixed: false, resizable: true, defaultWidth: 130},
    {columnId: 'webcastDialIn', fixed: false, resizable: true, defaultWidth: 120},
    {columnId: 'dialInDetails', fixed: false, resizable: true, defaultWidth: 180},
    {columnId: 'country',       fixed: false, resizable: true, defaultWidth: 110},
    {columnId: 'timeZone',      fixed: false, resizable: true, defaultWidth: 120},
    {columnId: 'status',           fixed: false, resizable: true, defaultWidth: 100},
    {columnId: 'notes',             fixed: false, resizable: true, defaultWidth: 120},
    {columnId: 'screenshot',        fixed: false, resizable: true, defaultWidth: 120},
    {columnId: 'eventTime',         fixed: false, resizable: true, defaultWidth: 100},
    {columnId: 'validationSource',  fixed: false, resizable: true, defaultWidth: 130},
    {columnId: 'infoType',          fixed: false, resizable: true, defaultWidth: 140},
    {columnId: 'isReplayAvailable', fixed: false, resizable: true, defaultWidth: 130},
    {columnId: 'replayExpiryDate',  fixed: false, resizable: true, defaultWidth: 140},
    {columnId: 'address',           fixed: false, resizable: true, defaultWidth: 150},
];

const POPUP_COLUMN_LABELS: Record<string, string> = {
    modifiedDate:   'Modification Date',
    daName:         'DA Name',
    eventName:      'Event Name',
    eventType:      'Event Type',
    beginDate:      'Effective Begining Date',
    endDate:        'Effective End Date',
    flag:           'Flag(E,A,C)',
    eventYear:      'Event Year',
    eventDetailUrl: 'Event Detail URL',
    webcastUrl:     'Webcast URL',
    webcastDialIn:  'Webcast Dial-in',
    dialInDetails:  'Dial-In Details',
    country:        'Country',
    timeZone:       'Time Zone',
    status:            'Status',
    notes:             'Notes',
    screenshot:        'Screenshot',
    eventTime:         'Event Time',
    validationSource:  'Validation Source',
    infoType:          'Info Type',
    isReplayAvailable: 'Replay Available',
    replayExpiryDate:  'Replay Expiry Date',
    address:           'Address',
};

const POPUP_TOTAL_WIDTH = POPUP_COLUMNS.reduce((sum, c) => sum + (c.defaultWidth ?? 120), 0);

// ── Helpers ──

/** Shape of a single log entry returned by /api/events/log */
export interface EventLogEntry {
    modificationDate?:  string;
    modifiedBy?:        string;
    event?:             string;
    eventType?:         string;
    beginingDate?:      string;
    endDate?:           string;
    eventDateStatus?:   string;
    eventYear?:         string;
    eventDetailUrl?:    string | null;
    webcastUrl?:        string;
    webcastPhone?:      string;
    dialInDetails?:     string;
    countryName?:       string;
    timeZoneName?:      string;
    statusName?:        string;
    note?:              string;
    imagePath?:         string | null;
    isReplayAvailable?: boolean;
    replayExpiryDate?:  string;
    eventTime?:         string;
    validationSource?:  string;
    infoType?:          string;
    address?:           string;
}

/** Fetch log entries for a specific event via /api/events/log?eventId={id} */
const fetchEventLog = async (eventId: number | string): Promise<EventLogEntry[]> => {
    const params = new URLSearchParams({eventId: String(eventId)});
    const response = await fetch(`${ROOT}/api/events/log?${params}`, {headers: {Accept: 'application/json'}});
    if (!response.ok) {
        const err = await response.json().catch(() => ({message: `HTTP ${response.status}`}));
        throw new Error(err?.message ?? 'Failed to fetch event log');
    }
    const data = await response.json();
    // Handle both array response and wrapped { logs/events/data: [...] } response
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.logs))   return data.logs;
    if (Array.isArray(data?.events)) return data.events;
    if (Array.isArray(data?.data))   return data.data;
    return [];
};

const mapLogToRows = (logs: EventLogEntry[]): RowData[] =>
    logs.map((entry, idx) => ({
        id: idx,
        columnValues: {
            modifiedDate:   entry.modificationDate ?? '',
            daName:         entry.modifiedBy ?? '',
            eventName:      entry.event ?? '',
            eventType:      entry.eventType ?? '',
            beginDate:      entry.beginingDate ?? '',
            endDate:        entry.endDate ?? '',
            flag:           entry.eventDateStatus ?? '',
            eventYear:      entry.eventYear ?? '',
            eventDetailUrl: entry.eventDetailUrl ?? '',
            webcastUrl:     entry.webcastUrl ?? '',
            webcastDialIn:  entry.webcastPhone ?? '',
            dialInDetails:  entry.dialInDetails ?? '',
            country:        entry.countryName ?? '',
            timeZone:       entry.timeZoneName ?? '',
            status:            entry.statusName ?? '',
            notes:             entry.note ?? '',
            screenshot:        entry.imagePath ?? '',
            eventTime:         entry.eventTime ?? '',
            validationSource:  entry.validationSource ?? '',
            infoType:          entry.infoType ?? '',
            isReplayAvailable: entry.isReplayAvailable != null ? String(entry.isReplayAvailable) : '',
            replayExpiryDate:  entry.replayExpiryDate ?? '',
            address:           entry.address ?? '',
        },
    }));

// ── StatusPopup ──

interface StatusPopupProps {
    /** The eventId whose log should be displayed */
    eventId: number | string;
    onClose: () => void;
}

const StatusPopup: React.FC<StatusPopupProps> = ({eventId, onClose}) => {
    const [logs, setLogs]     = useState<EventLogEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError]   = useState<string | null>(null);

    // Load log entries for this event once
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        fetchEventLog(eventId)
            .then(data => {
                if (!cancelled) setLogs(data);
            })
            .catch(err => {
                if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load event log');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [eventId]);

    // Close on Escape key
    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onClose]);

    const tableData = useMemo(
        () => ({columns: POPUP_COLUMNS, rows: mapLogToRows(logs)}),
        [logs]
    );

    const renderHeaders: RenderHeaders = useCallback(
        ({column}, cellOptions: CellOptions) => (
            <Cell {...cellOptions} column={column} resizable>
                <TableHeader indent={Indent.NONE}>
                    {POPUP_COLUMN_LABELS[column.columnId] ?? column.columnId}
                </TableHeader>
            </Cell>
        ),
        []
    );

    const renderRows: RenderRows = useCallback(
        (rowsParams, cellOptions: CellOptions) => {
            const colId  = rowsParams.column.columnId;
            const value  = rowsParams.row.columnValues[colId];
            const strVal = String(value ?? '');

            // Highlight empty Flag cells and Need-to-check Status cells in yellow
            const needsHighlight =
                (colId === 'flag'   && strVal.trim() === '') ||
                (colId === 'status' && strVal.toLowerCase().includes('need'));

            return (
                <Cell {...cellOptions} key={colId} className={needsHighlight ? cellHighlight : undefined}>
                    {strVal}
                </Cell>
            );
        },
        []
    );

    return (
        <div
            className={popupOverlay}
            onClick={onClose}
            role="presentation"
        >
            {/* Stop click propagation so clicking inside the dialog doesn't close it */}
            <div
                className={popupContainer}
                role="dialog"
                aria-modal="true"
                aria-labelledby="status-popup-title"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className={popupHeader}>
                    <span id="status-popup-title" className={popupTitle}>
                        Logs
                    </span>
                    <button
                        className={popupCloseButton}
                        onClick={onClose}
                        aria-label="Close"
                        type="button"
                    >
                        ✕
                    </button>
                </div>

                {/* Body */}
                <div className={popupBody}>
                    {/* Loading */}
                    {loading && (
                        <div className={popupLoadingContainer}>
                            <Loading />
                            <p>Loading events...</p>
                        </div>
                    )}

                    {/* Error */}
                    {error && !loading && (
                        <div className={popupErrorContainer}>
                            <strong>Error:</strong> {error}
                        </div>
                    )}

                    {/* Empty */}
                    {!loading && !error && logs.length === 0 && (
                        <div className={popupEmptyContainer}>
                            <p>No log entries found for event <strong>{eventId}</strong>.</p>
                        </div>
                    )}

                    {/* Data Table */}
                    {!loading && !error && logs.length > 0 && (
                        <div className={popupTableWrapper}>
                            <DataTable
                                style={{width: '100%', minWidth: POPUP_TOTAL_WIDTH}}
                                data={tableData}
                                containerEventListenersType="mainHeaderContainer:resize"
                                viewportHeight={400}
                                tableSize="DEFAULT"
                                rowsVirtualization={true}
                                minRowsForVirtualization={1}
                                minCellsForVirtualization={1}
                                overscanCellsRatio={1.05}
                                overscanRows={5}
                                reordering={{disabled: true, onChange: () => {}}}
                                headers={renderHeaders}
                                renderRows={renderRows}
                            />
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className={popupFooter}>
                    <button type="button" className={popupFooterClose} onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StatusPopup;
